/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaArrowLeft,
  FaExclamationTriangle,
  FaTimes,
  FaChevronDown,
} from 'react-icons/fa'
import { FaScaleBalanced } from 'react-icons/fa6'
import { ThreeDot } from 'react-loading-indicators'
import { XLSX, buildCorporateSheet } from '../../utils/excelCorporateStyle'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formatCurrencyBr } from '../../constantes'
import {
  ValidacaoSelloutParams,
  ValidacaoSelloutResponse,
  AlertSeverity,
  MetaVariationSeverity,
} from './types'
import {
  appendCorporateMensalSheets,
  appendCorporatePlanoVendaDiretaSheet,
  appendCorporateCoberturaAnoCategoriasSheet,
} from './exportRevisaoGrupo'
import {
  PageWrapper,
  TopHeaderBar,
  HeaderLeft,
  HeaderCenter,
  HeaderRight,
  SubHeaderBar,
  ResultTitle,
  FilterTagsRow,
  FilterTag,
  ActionButton,
  BackButton,
  SearchBar,
  SummaryCardsRow,
  SummaryCard,
  SummaryLabel,
  SummaryValue,
  MainGrid,
  GridColumn,
  ColumnHeader,
  ColumnCompareButton,
  ColumnTotalHeader,
  ColumnTotalTitle,
  ColumnTotalMetrics,
  ColumnBody,
  TotalMetricItem,
  FamiliaRow,
  FamiliaName,
  FamiliaMetrics,
  MetricItem,
  ShareBar,
  EmptyState,
  AlertBadge,
  ComparisonTag,
  MetaComparisonTag,
  SummaryCardMetaInfo,
  CompareModalOverlay,
  CompareModalContainer,
  CompareModalHeader,
  CompareModalCloseBtn,
  CompareModalSummaryCards,
  CompareSummaryCard,
  CompareModalBody,
  CompareTable,
  PotentialGapBadge,
  FamiliaHeader,
  FamiliaTitleGroup,
  SkuBadge,
  ExpandChevron,
  SkuListContainer,
  SkuRow,
  SkuInfoGroup,
  SkuCode,
  SkuDesc,
  SkuMetrics,
  SkuMetricValue,
  ToggleAllSkusButton,
} from './styles'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

// ─── Comparativo com a Média (Estoque e Modais) ────────────────────────────────

export interface VariationAlertInfo {
  hasAlert: boolean
  severity: AlertSeverity | null
  diffPercent: number
  formattedPercent: string
  tooltipText: string
}

/**
 * Calcula severidade de alerta vs média de 3 meses:
 * - < 5%: Normal
 * - 5% a 25%: Atenção (Amarelo)
 * - > 25%: Crítico (Vermelho)
 */
export function getVariationAlert(diff: number | undefined): VariationAlertInfo | null {
  if (typeof diff !== 'number' || isNaN(diff)) return null

  const absDiff = Math.abs(diff)
  const isRed = absDiff > 25
  const isYellow = absDiff >= 5 && absDiff <= 25
  const hasAlert = isRed || isYellow
  const severity: AlertSeverity | null = isRed ? 'red' : isYellow ? 'yellow' : null
  const sign = diff > 0 ? '+' : ''
  const formattedPercent = `${sign}${diff.toFixed(1)}%`

  const tooltipText = hasAlert
    ? isRed
      ? `Alerta Crítico: variação de ${formattedPercent} em relação à média de 3 meses (desvio superior a 25%)`
      : `Alerta de Atenção: variação de ${formattedPercent} em relação à média de 3 meses (desvio entre 5% e 25%)`
    : `Variação de ${formattedPercent} em relação à média de 3 meses`

  return {
    hasAlert,
    severity,
    diffPercent: diff,
    formattedPercent,
    tooltipText,
  }
}

/**
 * Renderiza indicador de variação vs média (usado em Estoque e nos Modais de Comparação)
 */
function renderVariationIndicator(alertInfo: VariationAlertInfo | null) {
  if (!alertInfo) return null

  const dir = alertInfo.diffPercent > 0 ? 'up' : alertInfo.diffPercent < 0 ? 'down' : 'neutral'
  const arrow = alertInfo.diffPercent > 0 ? '▲' : alertInfo.diffPercent < 0 ? '▼' : '•'

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        verticalAlign: 'middle',
      }}
      title={alertInfo.tooltipText}
    >
      <ComparisonTag $direction={dir} $severity={alertInfo.severity}>
        <span style={{ fontSize: '0.62rem' }}>{arrow}</span>
        <span>{alertInfo.formattedPercent}</span>
        <span style={{ fontSize: '0.62rem', opacity: 0.65, fontWeight: 500 }}>vs méd.</span>
      </ComparisonTag>
      {alertInfo.hasAlert && alertInfo.severity && (
        <AlertBadge $severity={alertInfo.severity}>
          <FaExclamationTriangle size={7} />
          <span>!</span>
        </AlertBadge>
      )}
    </span>
  )
}

// ─── Comparativo com a Meta (Threshold de 1,5%) ────────────────────────────────

export interface MetaVariationInfo {
  diffPercent: number
  formattedPercent: string
  severity: MetaVariationSeverity
  arrow: string
  tooltipText: string
}

/**
 * Calcula variação e cor em relação à Meta:
 * - > +1.5%: Verde (Superou a meta)
 * - < -1.5%: Vermelho (Abaixo da meta)
 * - entre -1.5% e +1.5%: Amarelo acinzentado discreto (Em linha com a meta)
 */
export function getMetaVariationInfo(
  diff: number | undefined,
  metaValue?: number,
  atingimentoPercent?: number,
): MetaVariationInfo | null {
  if (typeof diff !== 'number' || isNaN(diff)) return null

  let severity: MetaVariationSeverity
  let arrow: string

  if (diff > 1.5) {
    severity = 'green'
    arrow = '▲'
  } else if (diff < -1.5) {
    severity = 'red'
    arrow = '▼'
  } else {
    severity = 'subtle-yellow'
    arrow = '•'
  }

  const sign = diff > 0 ? '+' : ''
  const formattedPercent = `${sign}${diff.toFixed(1)}%`

  let tooltipText = `Variação vs Meta: ${formattedPercent}`
  if (typeof metaValue === 'number') {
    tooltipText += ` (Meta: ${metaValue.toLocaleString('pt-BR')})`
  }
  if (typeof atingimentoPercent === 'number') {
    tooltipText += ` — Atingimento: ${atingimentoPercent.toFixed(1)}%`
  }

  return {
    diffPercent: diff,
    formattedPercent,
    severity,
    arrow,
    tooltipText,
  }
}

/**
 * Renderiza indicador de variação vs Meta
 */
function renderMetaVariationIndicator(metaInfo: MetaVariationInfo | null) {
  if (!metaInfo) return null

  return (
    <MetaComparisonTag $severity={metaInfo.severity} title={metaInfo.tooltipText}>
      <span style={{ fontSize: '0.62rem' }}>{metaInfo.arrow}</span>
      <span>{metaInfo.formattedPercent}</span>
      <span style={{ fontSize: '0.6rem', opacity: 0.65, fontWeight: 500 }}>vs meta</span>
    </MetaComparisonTag>
  )
}

interface Props {
  data: ValidacaoSelloutResponse
  params: ValidacaoSelloutParams
  loading: boolean
  onBack: () => void
}

export default function ResultsStep({ data, params, loading, onBack }: Props) {
  const [search, setSearch] = useState('')
  const [isGoingBack, setIsGoingBack] = useState(false)
  const [activeModal, setActiveModal] = useState<'categorias' | 'cobertura' | null>(null)
  const [expandedCategorias, setExpandedCategorias] = useState<Set<string>>(new Set())
  const [expandedEstoque, setExpandedEstoque] = useState<Set<string>>(new Set())
  const [expandedCobertura, setExpandedCobertura] = useState<Set<string>>(new Set())
  const dashRef = useRef<HTMLDivElement>(null)

  // Handlers para expansão/recolhimento de SKUs por coluna
  const toggleCategoria = (familia: string) => {
    setExpandedCategorias((prev) => {
      const next = new Set(prev)
      if (next.has(familia)) next.delete(familia)
      else next.add(familia)
      return next
    })
  }

  const toggleAllCategorias = () => {
    if (expandedCategorias.size === filteredSellout.length) {
      setExpandedCategorias(new Set())
    } else {
      setExpandedCategorias(new Set(filteredSellout.map((f) => f.familia)))
    }
  }

  const toggleEstoque = (familia: string) => {
    setExpandedEstoque((prev) => {
      const next = new Set(prev)
      if (next.has(familia)) next.delete(familia)
      else next.add(familia)
      return next
    })
  }

  const toggleAllEstoque = () => {
    if (expandedEstoque.size === filteredEstoque.length) {
      setExpandedEstoque(new Set())
    } else {
      setExpandedEstoque(new Set(filteredEstoque.map((f) => f.familia)))
    }
  }

  const toggleCobertura = (familia: string) => {
    setExpandedCobertura((prev) => {
      const next = new Set(prev)
      if (next.has(familia)) next.delete(familia)
      else next.add(familia)
      return next
    })
  }

  const toggleAllCobertura = () => {
    if (expandedCobertura.size === filteredCobertura.length) {
      setExpandedCobertura(new Set())
    } else {
      setExpandedCobertura(new Set(filteredCobertura.map((f) => f.familia)))
    }
  }

  // Fecha modal ao pressionar ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null)
    }
    if (activeModal) {
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeModal])

  const handleBack = () => {
    if (isGoingBack) return
    setIsGoingBack(true)
    setTimeout(() => {
      onBack()
    }, 220)
  }

  // ── Tags de filtro aplicado ─────────────────────────────────────────────────
  const filterTags = useMemo(() => {
    const isYtd = params.tipoPeriodo === 'ytd' || Boolean(data.resumo?.isYtd)

    if (data.filtrosAplicados) {
      const f = data.filtrosAplicados
      const tags: string[] = []
      let periodo = ''
      if (isYtd) {
        if (params.ytdModo === 'tudo') {
          periodo = `YTD: Jan a ${MONTH_NAMES[params.mes] || f.mes}/${params.ano}`
        } else if (params.mesesYtd && params.mesesYtd.length > 0) {
          const mesesStr = params.mesesYtd.map((m) => MONTH_NAMES[m]?.substring(0, 3)).join(', ')
          periodo = `YTD (${mesesStr})/${params.ano}`
        } else {
          periodo = `YTD (Acumulado)/${params.ano}`
        }
      } else {
        periodo = f.semana
          ? `${f.mes}/${f.ano} (${f.semana})`
          : `${MONTH_NAMES[params.mes]}/${params.ano}`
      }
      tags.push(periodo)
      if (f.distribuidor) tags.push(`Dist.: ${f.distribuidor}`)
      if (f.cidade) tags.push(`Cidade: ${f.cidade}`)
      if (f.cliente && f.cliente !== 'Todos') tags.push(f.cliente)
      return tags
    }

    const tags: string[] = []
    let periodo = ''
    if (isYtd) {
      if (params.ytdModo === 'tudo') {
        periodo = `YTD: Jan a ${MONTH_NAMES[params.mes]}/${params.ano}`
      } else if (params.mesesYtd && params.mesesYtd.length > 0) {
        const mesesStr = params.mesesYtd.map((m) => MONTH_NAMES[m]?.substring(0, 3)).join(', ')
        periodo = `YTD (${mesesStr})/${params.ano}`
      } else {
        periodo = `YTD (Acumulado)/${params.ano}`
      }
    } else {
      const mesNome = MONTH_NAMES[params.mes]
      const isFullMonth = params.mesCompleto || params.semanas.length === 4 || params.semanas.length === 0
      const sortedWeeks = [...params.semanas].sort((a, b) => a - b)
      periodo = isFullMonth
        ? `${mesNome}/${params.ano} (Mês Completo)`
        : `${mesNome}/${params.ano} — Sem. ${sortedWeeks.join(', ')}`
    }
    tags.push(periodo)
    if (params.distribuidorIds.length > 0)
      tags.push(`${params.distribuidorIds.length} distribuidor(es)`)
    else
      tags.push('Todos os distribuidores')
    if (params.cidades.length > 0)
      tags.push(`${params.cidades.length} cidade(s)`)
    else
      tags.push('Todas as cidades')
    return tags
  }, [data.filtrosAplicados, data.resumo?.isYtd, params])

  // ── Filtro de busca aplicado nas 3 colunas (por família e por SKU/descrição) ─────────
  const term = search.trim().toLowerCase()

  const filteredSellout = useMemo(
    () =>
      data.selloutPorFamilia.filter(
        (i) =>
          i.familia.toLowerCase().includes(term) ||
          Boolean(
            i.skus &&
              i.skus.some(
                (s) =>
                  s.sku.toLowerCase().includes(term) ||
                  s.descricao.toLowerCase().includes(term),
              ),
          ),
      ),
    [data, term],
  )
  const filteredEstoque = useMemo(
    () =>
      data.estoquePorFamilia.filter(
        (i) =>
          i.familia.toLowerCase().includes(term) ||
          Boolean(
            i.skus &&
              i.skus.some(
                (s) =>
                  s.sku.toLowerCase().includes(term) ||
                  s.descricao.toLowerCase().includes(term),
              ),
          ),
      ),
    [data, term],
  )
  const filteredCobertura = useMemo(
    () =>
      data.coberturaPorFamilia.filter(
        (i) =>
          i.familia.toLowerCase().includes(term) ||
          Boolean(
            i.skus &&
              i.skus.some(
                (s) =>
                  s.sku.toLowerCase().includes(term) ||
                  s.descricao.toLowerCase().includes(term),
              ),
          ),
      ),
    [data, term],
  )

  // ── Totais calculados por coluna ────────────────────────────────────────────
  const totals = useMemo(() => {
    const isSearching = term.length > 0

    const selloutRS = isSearching
      ? filteredSellout.reduce((acc, curr) => acc + curr.valorRS, 0)
      : (data.resumo?.sellout_total_valor ?? data.totalSelloutRS)

    const selloutCX = isSearching
      ? filteredSellout.reduce((acc, curr) => acc + curr.caixas, 0)
      : (data.resumo?.sellout_total_caixas ?? data.totalSelloutCX)

    const estoqueCX = isSearching
      ? filteredEstoque.reduce((acc, curr) => acc + curr.caixas, 0)
      : Number(data.resumo?.estoque_total_caixas ?? data.totalEstoqueCX ?? 0)

    const estoqueRS = isSearching
      ? filteredEstoque.reduce((acc, curr) => acc + curr.valorRS, 0)
      : Number(data.resumo?.estoque_total_valor ?? data.totalEstoqueRS ?? 0)

    const coberturaClientes = isSearching
      ? filteredCobertura.reduce((acc, curr) => acc + curr.clientesPositivados, 0)
      : Number(data.resumo?.cobertura_total_clientes ?? 0)

    return {
      selloutRS,
      selloutCX,
      estoqueCX,
      estoqueRS,
      coberturaClientes,
    }
  }, [data, filteredSellout, filteredEstoque, filteredCobertura, term])

  // ── Variações vs Meta nos Totais Consolidados ──────────────────────────────
  const totalMetaAlertRS = useMemo(
    () =>
      getMetaVariationInfo(
        data.resumo?.sellout_variacao_meta_valor_percentual,
        data.resumo?.sellout_meta_valor,
        data.resumo?.sellout_atingimento_valor_percentual,
      ),
    [
      data.resumo?.sellout_variacao_meta_valor_percentual,
      data.resumo?.sellout_meta_valor,
      data.resumo?.sellout_atingimento_valor_percentual,
    ],
  )

  const totalMetaAlertCX = useMemo(
    () =>
      getMetaVariationInfo(
        data.resumo?.sellout_variacao_meta_percentual,
        data.resumo?.sellout_meta_caixas,
        data.resumo?.sellout_atingimento_caixas_percentual,
      ),
    [
      data.resumo?.sellout_variacao_meta_percentual,
      data.resumo?.sellout_meta_caixas,
      data.resumo?.sellout_atingimento_caixas_percentual,
    ],
  )



  // ── Variações vs Média 3M (Estoque e Modais) ────────────────────────────────
  const totalAlertRS = useMemo(
    () => getVariationAlert(data.resumo?.sellout_variacao_valor_percentual),
    [data.resumo?.sellout_variacao_valor_percentual],
  )
  const totalAlertCX = useMemo(
    () => getVariationAlert(data.resumo?.sellout_variacao_percentual),
    [data.resumo?.sellout_variacao_percentual],
  )
  const totalAlertEstoque = useMemo(
    () => getVariationAlert(data.resumo?.estoque_variacao_percentual),
    [data.resumo?.estoque_variacao_percentual],
  )
  const totalAlertCobertura = useMemo(
    () => getVariationAlert(data.resumo?.cobertura_variacao_percentual),
    [data.resumo?.cobertura_variacao_percentual],
  )

  // ── Exportar Excel Corporativo GID ──────────────────────────────────────────
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new()
    const periodLabel = filterTags[0] || `${MONTH_NAMES[params.mes]} de ${params.ano}`
    const distLabel = 'Distribuidores Selecionados'

    // 1. Resumo
    const rowsResumo = [
      ['Total Sellout Faturado (R$)', data.totalSelloutRS],
      ['Total Sellout Comercializado (CX)', data.totalSelloutCX],
      ['Meta de Sellout Estabelecida (R$)', data.resumo?.sellout_meta_valor ?? 0],
      ['Meta de Sellout em Volume (CX)', data.resumo?.sellout_meta_caixas ?? 0],
    ]
    const wsResumo = buildCorporateSheet({
      reportTitle: 'Validação de Sellout — Resumo Consolidado',
      periodLabel,
      distributorLabel: distLabel,
      headers: [
        { label: 'Indicador de Sellout', align: 'left' },
        { label: 'Valor Consolidado', align: 'right' },
      ],
      data: rowsResumo,
    })
    XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo')

    // 2. Categorias / Famílias (com quebra por SKU)
    const rowsSell: (string | number)[][] = []
    data.selloutPorFamilia.forEach((i) => {
      rowsSell.push([
        i.familia,
        i.valorRS,
        i.caixas,
        i.meta_valor ?? 0,
        i.meta_caixas ?? 0,
        i.variacao_meta_valor_percentual !== undefined ? `${i.variacao_meta_valor_percentual}%` : '—',
        i.variacao_meta_percentual !== undefined ? `${i.variacao_meta_percentual}%` : '—',
        i.media_3m_valor ?? 0,
        i.variacao_valor_percentual !== undefined ? `${i.variacao_valor_percentual}%` : '—',
        i.media_3m_caixas ?? 0,
        i.variacao_percentual !== undefined ? `${i.variacao_percentual}%` : '—',
        i.sharePercent,
      ])
      if (i.skus && i.skus.length > 0) {
        i.skus.forEach((s) => {
          rowsSell.push([
            `   ↳ [${s.sku}] ${s.descricao}`,
            s.valorRS,
            s.caixas,
            '—',
            '—',
            '—',
            '—',
            s.media_3m_valor ?? 0,
            s.variacao_valor_percentual !== undefined ? `${s.variacao_valor_percentual}%` : '—',
            s.media_3m_caixas ?? 0,
            s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
            s.sharePercent,
          ])
        })
      }
    })
    const totalSell = [
      'TOTAL GERAL',
      data.totalSelloutRS,
      data.totalSelloutCX,
      data.resumo?.sellout_meta_valor ?? 0,
      data.resumo?.sellout_meta_caixas ?? 0,
      data.resumo?.sellout_variacao_meta_valor_percentual !== undefined ? `${data.resumo.sellout_variacao_meta_valor_percentual}%` : '—',
      data.resumo?.sellout_variacao_meta_percentual !== undefined ? `${data.resumo.sellout_variacao_meta_percentual}%` : '—',
      data.resumo?.sellout_media_3m_valor ?? 0,
      data.resumo?.sellout_variacao_valor_percentual !== undefined ? `${data.resumo.sellout_variacao_valor_percentual}%` : '—',
      data.resumo?.sellout_media_3m_caixas ?? 0,
      data.resumo?.sellout_variacao_percentual !== undefined ? `${data.resumo.sellout_variacao_percentual}%` : '—',
      100,
    ]
    const headersSell = [
      { label: 'Família / Categoria', align: 'left' as const },
      { label: 'Sellout (R$)', align: 'right' as const },
      { label: 'Sellout (CX)', align: 'right' as const },
      { label: 'Meta (R$)', align: 'right' as const },
      { label: 'Meta (CX)', align: 'right' as const },
      { label: 'Var. Meta % (R$)', align: 'center' as const },
      { label: 'Var. Meta % (CX)', align: 'center' as const },
      { label: 'Média 3M (R$)', align: 'right' as const },
      { label: 'Var. 3M % (R$)', align: 'center' as const },
      { label: 'Média 3M (CX)', align: 'right' as const },
      { label: 'Var. 3M % (CX)', align: 'center' as const },
      { label: 'Share (%)', align: 'right' as const },
    ]
    const wsSell = buildCorporateSheet({
      reportTitle: 'Validação de Sellout — Desempenho por Categoria e SKU',
      periodLabel,
      distributorLabel: distLabel,
      headers: headersSell,
      data: rowsSell,
      totalsRow: totalSell,
    })
    XLSX.utils.book_append_sheet(wb, wsSell, 'Categorias')

    // 3. Estoque (com quebra por SKU)
    const totalEstCX = Number(data.resumo?.estoque_total_caixas ?? data.totalEstoqueCX ?? 0)
    const totalEstRS = Number(data.resumo?.estoque_total_valor ?? data.totalEstoqueRS ?? 0)
    const rowsEst: (string | number)[][] = []
    data.estoquePorFamilia.forEach((i) => {
      rowsEst.push([
        i.familia,
        i.caixas,
        i.valorRS,
        i.media_3m_caixas ?? 0,
        i.variacao_percentual !== undefined ? `${i.variacao_percentual}%` : '—',
      ])
      if (i.skus && i.skus.length > 0) {
        i.skus.forEach((s) => {
          rowsEst.push([
            `   ↳ [${s.sku}] ${s.descricao}`,
            s.caixas,
            s.valorRS,
            s.media_3m_caixas ?? 0,
            s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
          ])
        })
      }
    })
    const totalEst = [
      'TOTAL GERAL',
      totalEstCX,
      totalEstRS,
      data.resumo?.estoque_media_3m_caixas ?? 0,
      data.resumo?.estoque_variacao_percentual !== undefined ? `${data.resumo.estoque_variacao_percentual}%` : '—',
    ]
    const headersEst = [
      { label: 'Família / Categoria', align: 'left' as const },
      { label: 'Estoque (CX)', align: 'right' as const },
      { label: 'Estoque (R$)', align: 'right' as const },
      { label: 'Média 3M (CX)', align: 'right' as const },
      { label: 'Var. % CX', align: 'center' as const },
    ]
    const wsEst = buildCorporateSheet({
      reportTitle: 'Validação de Estoque — Posição por Categoria e SKU',
      periodLabel,
      distributorLabel: distLabel,
      headers: headersEst,
      data: rowsEst,
      totalsRow: totalEst,
    })
    XLSX.utils.book_append_sheet(wb, wsEst, 'Estoque')

    // 4. Cobertura (com quebra por SKU)
    const totalCob = Number(
      data.resumo?.cobertura_total_clientes ??
        data.coberturaPorFamilia.reduce((a, b) => a + b.clientesPositivados, 0),
    )
    const rowsCob: (string | number)[][] = []
    data.coberturaPorFamilia.forEach((i) => {
      rowsCob.push([
        i.familia,
        i.clientesPositivados,
        i.potencial_clientes ?? '—',
        i.gap_clientes ?? '—',
        i.media_3m_clientes ?? 0,
        i.variacao_percentual !== undefined ? `${i.variacao_percentual}%` : '—',
      ])
      if (i.skus && i.skus.length > 0) {
        i.skus.forEach((s) => {
          rowsCob.push([
            `   ↳ [${s.sku}] ${s.descricao}`,
            s.clientesPositivados,
            '—',
            '—',
            s.media_3m_clientes ?? 0,
            s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
          ])
        })
      }
    })
    const totalCobRow = [
      'TOTAL GERAL',
      totalCob,
      data.resumo?.cobertura_potencial_clientes ?? '—',
      data.resumo?.cobertura_gap_clientes ?? '—',
      data.resumo?.cobertura_media_3m_clientes ?? 0,
      data.resumo?.cobertura_variacao_percentual !== undefined ? `${data.resumo.cobertura_variacao_percentual}%` : '—',
    ]
    const headersCob = [
      { label: 'Família / Categoria', align: 'left' as const },
      { label: 'Clientes Positivados', align: 'right' as const },
      { label: 'Potencial (Cidades Atuação)', align: 'right' as const },
      { label: 'Gap de Cobertura', align: 'right' as const },
      { label: 'Média 3M Clientes', align: 'right' as const },
      { label: 'Var. %', align: 'center' as const },
    ]
    const wsCob = buildCorporateSheet({
      reportTitle: 'Validação de Cobertura — Positivação de Clientes por Categoria e SKU',
      periodLabel,
      distributorLabel: distLabel,
      headers: headersCob,
      data: rowsCob,
      totalsRow: totalCobRow,
    })
    XLSX.utils.book_append_sheet(wb, wsCob, 'Resumo Cobertura')

    // 5. Visão Consolidada de Todos os SKUs
    const skuMap: Record<
      string,
      {
        familia: string
        sku: string
        descricao: string
        selloutRS: number
        selloutCX: number
        shareCat: number
        media3mSelloutRS: number
        var3mSelloutRS: string | number
        media3mSelloutCX: number
        var3mSelloutCX: string | number
        estoqueCX: number
        estoqueRS: number
        media3mEstoqueCX: number
        var3mEstoqueCX: string | number
        coberturaClientes: number
        media3mCobClientes: number
        var3mCobClientes: string | number
      }
    > = {}

    data.selloutPorFamilia.forEach((f) => {
      ;(f.skus || []).forEach((s) => {
        skuMap[s.sku] = {
          familia: f.familia,
          sku: s.sku,
          descricao: s.descricao,
          selloutRS: s.valorRS,
          selloutCX: s.caixas,
          shareCat: s.sharePercent,
          media3mSelloutRS: s.media_3m_valor ?? 0,
          var3mSelloutRS: s.variacao_valor_percentual !== undefined ? `${s.variacao_valor_percentual}%` : '—',
          media3mSelloutCX: s.media_3m_caixas ?? 0,
          var3mSelloutCX: s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
          estoqueCX: 0,
          estoqueRS: 0,
          media3mEstoqueCX: 0,
          var3mEstoqueCX: '—',
          coberturaClientes: s.clientesPositivados ?? 0,
          media3mCobClientes: 0,
          var3mCobClientes: '—',
        }
      })
    })

    data.estoquePorFamilia.forEach((f) => {
      ;(f.skus || []).forEach((s) => {
        if (!skuMap[s.sku]) {
          skuMap[s.sku] = {
            familia: f.familia,
            sku: s.sku,
            descricao: s.descricao,
            selloutRS: 0,
            selloutCX: 0,
            shareCat: 0,
            media3mSelloutRS: 0,
            var3mSelloutRS: '—',
            media3mSelloutCX: 0,
            var3mSelloutCX: '—',
            estoqueCX: s.caixas,
            estoqueRS: s.valorRS,
            media3mEstoqueCX: s.media_3m_caixas ?? 0,
            var3mEstoqueCX: s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
            coberturaClientes: 0,
            media3mCobClientes: 0,
            var3mCobClientes: '—',
          }
        } else {
          skuMap[s.sku].estoqueCX = s.caixas
          skuMap[s.sku].estoqueRS = s.valorRS
          skuMap[s.sku].media3mEstoqueCX = s.media_3m_caixas ?? 0
          skuMap[s.sku].var3mEstoqueCX = s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—'
        }
      })
    })

    data.coberturaPorFamilia.forEach((f) => {
      ;(f.skus || []).forEach((s) => {
        if (skuMap[s.sku]) {
          if (skuMap[s.sku].coberturaClientes === 0) {
            skuMap[s.sku].coberturaClientes = s.clientesPositivados
          }
          skuMap[s.sku].media3mCobClientes = s.media_3m_clientes ?? 0
          skuMap[s.sku].var3mCobClientes = s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—'
        }
      })
    })

    const rowsSkus = Object.values(skuMap)
      .sort((a, b) => b.selloutRS - a.selloutRS)
      .map((s) => [
        s.familia,
        s.sku,
        s.descricao,
        s.selloutRS,
        s.selloutCX,
        s.shareCat,
        s.media3mSelloutRS,
        s.var3mSelloutRS,
        s.media3mSelloutCX,
        s.var3mSelloutCX,
        s.estoqueCX,
        s.estoqueRS,
        s.media3mEstoqueCX,
        s.var3mEstoqueCX,
        s.coberturaClientes,
        s.media3mCobClientes,
        s.var3mCobClientes,
      ])

    const totalSkusRow = [
      'TOTAL GERAL',
      '—',
      `${rowsSkus.length} SKUs`,
      data.totalSelloutRS,
      data.totalSelloutCX,
      100,
      data.resumo?.sellout_media_3m_valor ?? 0,
      data.resumo?.sellout_variacao_valor_percentual !== undefined ? `${data.resumo.sellout_variacao_valor_percentual}%` : '—',
      data.resumo?.sellout_media_3m_caixas ?? 0,
      data.resumo?.sellout_variacao_percentual !== undefined ? `${data.resumo.sellout_variacao_percentual}%` : '—',
      totalEstCX,
      totalEstRS,
      data.resumo?.estoque_media_3m_caixas ?? 0,
      data.resumo?.estoque_variacao_percentual !== undefined ? `${data.resumo.estoque_variacao_percentual}%` : '—',
      totalCob,
      data.resumo?.cobertura_media_3m_clientes ?? 0,
      data.resumo?.cobertura_variacao_percentual !== undefined ? `${data.resumo.cobertura_variacao_percentual}%` : '—',
    ]

    const headersSkus = [
      { label: 'Família / Categoria', align: 'left' as const },
      { label: 'Código SKU', align: 'center' as const },
      { label: 'Descrição do Produto', align: 'left' as const },
      { label: 'Sellout (R$)', align: 'right' as const },
      { label: 'Sellout (CX)', align: 'right' as const },
      { label: 'Share Categoria (%)', align: 'right' as const },
      { label: 'Média 3M (R$)', align: 'right' as const },
      { label: 'Var. 3M % (R$)', align: 'center' as const },
      { label: 'Média 3M (CX)', align: 'right' as const },
      { label: 'Var. 3M % (CX)', align: 'center' as const },
      { label: 'Estoque (CX)', align: 'right' as const },
      { label: 'Estoque (R$)', align: 'right' as const },
      { label: 'Média 3M Estoque (CX)', align: 'right' as const },
      { label: 'Var. % Estoque', align: 'center' as const },
      { label: 'Clientes Positivados', align: 'right' as const },
      { label: 'Média 3M Clientes', align: 'right' as const },
      { label: 'Var. % Clientes', align: 'center' as const },
    ]

    const wsSkus = buildCorporateSheet({
      reportTitle: 'Validação de Sellout — Posição Detalhada por SKU',
      periodLabel,
      distributorLabel: distLabel,
      headers: headersSkus,
      data: rowsSkus,
      totalsRow: totalSkusRow,
    })
    XLSX.utils.book_append_sheet(wb, wsSkus, 'SKUs')

    // 6. Abas Mensais por Distribuidor e Família ('Sellout CX', 'Sellout R$' e 'Cobertura')
    appendCorporateMensalSheets(wb, data, periodLabel, distLabel)

    // 7. Aba 'Dados para Plano Venda Direta' (estrutura conforme modelo de Positivação 2027)
    if (data.planoVendaDireta && data.planoVendaDireta.length > 0) {
      appendCorporatePlanoVendaDiretaSheet(wb, data, periodLabel, distLabel)
    }

    // 8. Aba 'Cobertura Ano Categorias' (positivação única por categoria acumulada no ano + resumo financeiro)
    if (data.coberturaAnoCategorias) {
      appendCorporateCoberturaAnoCategoriasSheet(wb, data, periodLabel, distLabel)
    }

    XLSX.writeFile(wb, `validacao_sellout_${MONTH_NAMES[params.mes]}_${params.ano}.xlsx`)
  }

  // ── Exportar PDF ────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' })
    const tagStr = filterTags.join('  |  ')

    doc.setFontSize(16)
    doc.setTextColor(0, 33, 91)
    doc.text('Validação de Sellout', 14, 16)
    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text(tagStr, 14, 23)

    doc.setFontSize(11)
    doc.setTextColor(0, 33, 91)
    doc.text(
      `Total Sellout R$: ${formatCurrencyBr(data.totalSelloutRS)}    Total Sellout CX: ${data.totalSelloutCX.toLocaleString('pt-BR')} cx`,
      14,
      32,
    )

    // Categorias
    autoTable(doc, {
      startY: 38,
      head: [['Família', 'Sellout R$', 'Meta R$', 'Sellout CX', 'Meta CX', 'Share %']],
      body: [
        ...data.selloutPorFamilia.map((i) => [
          i.familia,
          formatCurrencyBr(i.valorRS),
          i.meta_valor ? formatCurrencyBr(i.meta_valor) : '—',
          i.caixas.toLocaleString('pt-BR'),
          i.meta_caixas ? i.meta_caixas.toLocaleString('pt-BR') : '—',
          `${i.sharePercent}%`,
        ]),
        [
          'TOTAL GERAL',
          formatCurrencyBr(data.totalSelloutRS),
          data.resumo?.sellout_meta_valor ? formatCurrencyBr(data.resumo.sellout_meta_valor) : '—',
          `${data.totalSelloutCX.toLocaleString('pt-BR')} cx`,
          data.resumo?.sellout_meta_caixas ? `${data.resumo.sellout_meta_caixas.toLocaleString('pt-BR')} cx` : '—',
          '100%',
        ],
      ],
      headStyles: { fillColor: [0, 33, 91], textColor: 255 },
      styles: { fontSize: 8 },
      margin: { left: 14 },
      tableWidth: 'auto',
    })

    const afterCateg = (doc as any).lastAutoTable.finalY + 8

    // Estoque
    const pdfEstCX = Number(data.resumo?.estoque_total_caixas ?? data.totalEstoqueCX ?? 0)
    const pdfEstRS = Number(data.resumo?.estoque_total_valor ?? data.totalEstoqueRS ?? 0)
    autoTable(doc, {
      startY: afterCateg,
      head: [['Família', 'Estoque CX', 'Estoque R$', 'Média 3M CX', 'Var. %']],
      body: [
        ...data.estoquePorFamilia.map((i) => [
          i.familia,
          i.caixas.toLocaleString('pt-BR'),
          formatCurrencyBr(i.valorRS),
          i.media_3m_caixas ? i.media_3m_caixas.toLocaleString('pt-BR') : '—',
          i.variacao_percentual !== undefined ? `${i.variacao_percentual}%` : '—',
        ]),
        [
          'TOTAL GERAL',
          `${pdfEstCX.toLocaleString('pt-BR')} cx`,
          formatCurrencyBr(pdfEstRS),
          data.resumo?.estoque_media_3m_caixas ? `${data.resumo.estoque_media_3m_caixas.toLocaleString('pt-BR')} cx` : '—',
          data.resumo?.estoque_variacao_percentual !== undefined ? `${data.resumo.estoque_variacao_percentual}%` : '—',
        ],
      ],
      headStyles: { fillColor: [0, 43, 128], textColor: 255 },
      styles: { fontSize: 8 },
      margin: { left: 14 },
    })

    const afterEst = (doc as any).lastAutoTable.finalY + 8

    // Cobertura
    const pdfCob =
      data.resumo?.cobertura_total_clientes ??
      data.coberturaPorFamilia.reduce((a, b) => a + b.clientesPositivados, 0)
    autoTable(doc, {
      startY: afterEst,
      head: [['Família', 'Positivados', 'Potencial', 'Gap', 'Média 3M', 'Var. %']],
      body: [
        ...data.coberturaPorFamilia.map((i) => [
          i.familia,
          i.clientesPositivados.toLocaleString('pt-BR'),
          i.potencial_clientes ? i.potencial_clientes.toLocaleString('pt-BR') : '—',
          i.gap_clientes !== undefined ? i.gap_clientes.toLocaleString('pt-BR') : '—',
          i.media_3m_clientes ? i.media_3m_clientes.toLocaleString('pt-BR') : '—',
          i.variacao_percentual !== undefined ? `${i.variacao_percentual}%` : '—',
        ]),
        [
          'TOTAL GERAL',
          `${pdfCob.toLocaleString('pt-BR')} clientes`,
          data.resumo?.cobertura_potencial_clientes ? `${data.resumo.cobertura_potencial_clientes.toLocaleString('pt-BR')}` : '—',
          data.resumo?.cobertura_gap_clientes !== undefined ? `${data.resumo.cobertura_gap_clientes.toLocaleString('pt-BR')}` : '—',
          data.resumo?.cobertura_media_3m_clientes ? `${data.resumo.cobertura_media_3m_clientes.toLocaleString('pt-BR')} clientes` : '—',
          data.resumo?.cobertura_variacao_percentual !== undefined ? `${data.resumo.cobertura_variacao_percentual}%` : '—',
        ],
      ],
      headStyles: { fillColor: [0, 24, 66], textColor: 255 },
      styles: { fontSize: 8 },
      margin: { left: 14 },
    })

    doc.save(`validacao_sellout_${MONTH_NAMES[params.mes]}_${params.ano}.pdf`)
  }

  // ── Skeleton de carregamento ────────────────────────────────────────────────
  if (loading) {
    return (
      <PageWrapper>
        <div style={{ paddingTop: 20 }}>
          <BackButton $isClicking={isGoingBack} onClick={handleBack}>
            <FaArrowLeft size={16} /> Voltar
          </BackButton>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '55vh',
            gap: 20,
          }}
        >
          <ThreeDot color="#a6c10b" size="medium" />
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
            Gerando resumo de validação...
          </span>
        </div>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper ref={dashRef}>
      {/* ── Barra superior com Voltar, Título Centralizado & Ações ───────── */}
      <TopHeaderBar>
        <HeaderLeft>
          <BackButton id="btn-back-filters" $isClicking={isGoingBack} onClick={handleBack}>
            <FaArrowLeft size={16} /> Voltar
          </BackButton>
        </HeaderLeft>

        <HeaderCenter>
          <ResultTitle id="result-title">
            Validação de <span>Sellout</span>
          </ResultTitle>
        </HeaderCenter>

        <HeaderRight>
          <ActionButton id="btn-export-excel" onClick={handleExportExcel}>
            <FaFileExcel size={13} /> Excel
          </ActionButton>
          <ActionButton id="btn-export-pdf" onClick={handleExportPDF}>
            <FaFilePdf size={13} /> PDF
          </ActionButton>
        </HeaderRight>
      </TopHeaderBar>

      {/* ── Sub-barra com Tags de Filtros e Busca ───────────────────────── */}
      <SubHeaderBar>
        <FilterTagsRow>
          {filterTags.map((tag) => (
            <FilterTag key={tag}>{tag}</FilterTag>
          ))}
        </FilterTagsRow>

        <SearchBar>
          <FaSearch />
          <input
            id="search-familia"
            placeholder="Buscar família..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchBar>
      </SubHeaderBar>

      {/* ── Cards de resumo comparando com a META ───────────────────────── */}
      <SummaryCardsRow>
        <SummaryCard id="card-total-rs">
          <SummaryLabel>Sellout Total em R$</SummaryLabel>
          <SummaryValue>{formatCurrencyBr(data.totalSelloutRS)}</SummaryValue>
          {totalMetaAlertRS ? (
            <SummaryCardMetaInfo $severity={totalMetaAlertRS.severity}>
              <span>{totalMetaAlertRS.arrow}</span>
              <span>{totalMetaAlertRS.formattedPercent} vs meta</span>
              {typeof data.resumo?.sellout_meta_valor === 'number' && (
                <span style={{ opacity: 0.8, fontWeight: 500, fontSize: '0.7rem' }}>
                  (Meta: {formatCurrencyBr(data.resumo.sellout_meta_valor)})
                </span>
              )}
              {typeof data.resumo?.sellout_atingimento_valor_percentual === 'number' && (
                <span style={{ opacity: 0.8, fontWeight: 600, fontSize: '0.7rem' }}>
                  • {data.resumo.sellout_atingimento_valor_percentual.toFixed(1)}% ating.
                </span>
              )}
            </SummaryCardMetaInfo>
          ) : (
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.45)' }}>
              Sem meta cadastrada no período
            </div>
          )}
        </SummaryCard>

        <SummaryCard id="card-total-cx">
          <SummaryLabel>Sellout Total em CX</SummaryLabel>
          <SummaryValue>
            {data.totalSelloutCX.toLocaleString('pt-BR')}{' '}
            <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>cx</span>
          </SummaryValue>
          {totalMetaAlertCX ? (
            <SummaryCardMetaInfo $severity={totalMetaAlertCX.severity}>
              <span>{totalMetaAlertCX.arrow}</span>
              <span>{totalMetaAlertCX.formattedPercent} vs meta</span>
              {typeof data.resumo?.sellout_meta_caixas === 'number' && (
                <span style={{ opacity: 0.8, fontWeight: 500, fontSize: '0.7rem' }}>
                  (Meta: {data.resumo.sellout_meta_caixas.toLocaleString('pt-BR')} cx)
                </span>
              )}
              {typeof data.resumo?.sellout_atingimento_caixas_percentual === 'number' && (
                <span style={{ opacity: 0.8, fontWeight: 600, fontSize: '0.7rem' }}>
                  • {data.resumo.sellout_atingimento_caixas_percentual.toFixed(1)}% ating.
                </span>
              )}
            </SummaryCardMetaInfo>
          ) : (
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.45)' }}>
              Sem meta cadastrada no período
            </div>
          )}
        </SummaryCard>

        <SummaryCard id="card-total-cobertura">
          <SummaryLabel>Cobertura Total & Potencial</SummaryLabel>
          <SummaryValue>
            {(data.resumo?.cobertura_total_clientes ?? totals.coberturaClientes).toLocaleString('pt-BR')}{' '}
            <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>clientes</span>
          </SummaryValue>
          {typeof data.resumo?.cobertura_potencial_clientes === 'number' && data.resumo.cobertura_potencial_clientes > 0 ? (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: '0.75rem' }}>
                <span style={{ color: '#a6c10b', fontWeight: 600 }}>
                  Potencial: {data.resumo.cobertura_potencial_clientes.toLocaleString('pt-BR')}
                </span>
                <span style={{ opacity: 0.5 }}>•</span>
                <span style={{ color: (data.resumo.cobertura_gap_clientes || 0) > 0 ? '#f59e0b' : '#10b981', fontWeight: 600 }}>
                  Gap: {(data.resumo.cobertura_gap_clientes || 0).toLocaleString('pt-BR')}
                </span>
                {typeof data.resumo.cobertura_atingimento_potencial_pct === 'number' && (
                  <>
                    <span style={{ opacity: 0.5 }}>•</span>
                    <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                      {data.resumo.cobertura_atingimento_potencial_pct.toFixed(1)}% ating.
                    </span>
                  </>
                )}
              </div>
              {typeof data.resumo.cidades_atuacao_count === 'number' && data.resumo.cidades_atuacao_count > 0 && (
                <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)' }}>
                  Base: {data.resumo.cidades_atuacao_count} {data.resumo.cidades_atuacao_count === 1 ? 'cidade com atuação' : 'cidades com atuação'}
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.45)' }}>
              Cobertura de clientes positivados
            </div>
          )}
        </SummaryCard>
      </SummaryCardsRow>

      {/* ── Grid principal de 3 colunas ────────────────────────────────── */}
      <MainGrid>
        {/* Coluna 1 — Categorias (Sellout vs Meta + Botão Balança Comparar Média) */}
        <GridColumn>
          <ColumnHeader>
            <div className="header-titles">
              <h2>Categorias</h2>
              <p>Sellout por família vs Meta</p>
            </div>
            {!data.resumo?.isYtd && (
              <ColumnCompareButton
                id="btn-compare-categorias"
                type="button"
                title="Comparar com Média de 3 Meses"
                onClick={() => setActiveModal('categorias')}
              >
                <FaScaleBalanced size={15} />
              </ColumnCompareButton>
            )}
          </ColumnHeader>

          <ColumnTotalHeader>
            <ColumnTotalTitle>
              <span>Total Categorias</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '0.72rem' }}>
                  {filteredSellout.length} {filteredSellout.length === 1 ? 'família' : 'famílias'}
                </span>
                {filteredSellout.some((f) => f.skus && f.skus.length > 0) && (
                  <ToggleAllSkusButton type="button" onClick={toggleAllCategorias}>
                    {expandedCategorias.size === filteredSellout.length ? 'Recolher SKUs' : 'Expandir SKUs'}
                  </ToggleAllSkusButton>
                )}
              </div>
            </ColumnTotalTitle>
            <ColumnTotalMetrics>
              <TotalMetricItem $accent title="Total Sellout R$">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{formatCurrencyBr(totals.selloutRS)}</span>
                  {renderMetaVariationIndicator(totalMetaAlertRS)}
                </div>
              </TotalMetricItem>
              <TotalMetricItem title="Total Sellout CX">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{totals.selloutCX.toLocaleString('pt-BR')} cx</span>
                  {renderMetaVariationIndicator(totalMetaAlertCX)}
                </div>
              </TotalMetricItem>
              <TotalMetricItem style={{ color: 'rgba(255, 255, 255, 0.5)' }}>100%</TotalMetricItem>
            </ColumnTotalMetrics>
          </ColumnTotalHeader>

          <ColumnBody>
            {filteredSellout.length === 0 ? (
              <EmptyState>
                <FaSearch size={22} />
                Nenhuma família encontrada.
              </EmptyState>
            ) : (
              filteredSellout.map((item, i) => {
                const itemMetaAlertRS = getMetaVariationInfo(
                  item.variacao_meta_valor_percentual,
                  item.meta_valor,
                  item.atingimento_valor_percentual,
                )
                const itemMetaAlertCX = getMetaVariationInfo(
                  item.variacao_meta_percentual,
                  item.meta_caixas,
                  item.atingimento_caixas_percentual,
                )

                const hasSkus = Boolean(item.skus && item.skus.length > 0)
                const isExpanded =
                  expandedCategorias.has(item.familia) ||
                  Boolean(
                    term.length > 0 &&
                      item.skus?.some(
                        (s) =>
                          s.sku.toLowerCase().includes(term) ||
                          s.descricao.toLowerCase().includes(term),
                      ),
                  )
                const skusToRender =
                  term.length > 0 && !item.familia.toLowerCase().includes(term)
                    ? (item.skus || []).filter(
                        (s) =>
                          s.sku.toLowerCase().includes(term) ||
                          s.descricao.toLowerCase().includes(term),
                      )
                    : item.skus || []

                return (
                  <FamiliaRow key={item.familia} $index={i}>
                    <FamiliaHeader
                      $clickable={hasSkus}
                      onClick={() => hasSkus && toggleCategoria(item.familia)}
                      title={hasSkus ? (isExpanded ? 'Recolher SKUs' : 'Expandir SKUs') : undefined}
                    >
                      <FamiliaTitleGroup>
                        <FamiliaName>{item.familia}</FamiliaName>
                        {hasSkus && <SkuBadge>{item.skus!.length} SKUs</SkuBadge>}
                      </FamiliaTitleGroup>
                      {hasSkus && (
                        <ExpandChevron $expanded={Boolean(isExpanded)}>
                          <FaChevronDown size={11} />
                        </ExpandChevron>
                      )}
                    </FamiliaHeader>
                    <FamiliaMetrics>
                      <MetricItem $accent>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{formatCurrencyBr(item.valorRS)}</span>
                          {renderMetaVariationIndicator(itemMetaAlertRS)}
                        </div>
                      </MetricItem>
                      <MetricItem>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{item.caixas.toLocaleString('pt-BR')} cx</span>
                          {renderMetaVariationIndicator(itemMetaAlertCX)}
                        </div>
                      </MetricItem>
                      <MetricItem>{item.sharePercent}%</MetricItem>
                    </FamiliaMetrics>
                    <ShareBar $percent={item.sharePercent} />
                    {isExpanded && hasSkus && (
                      <SkuListContainer>
                        {skusToRender.map((sku) => {
                          const skuAlertRS = getVariationAlert(sku.variacao_valor_percentual)
                          const skuAlertCX = getVariationAlert(sku.variacao_percentual)

                          return (
                            <SkuRow key={sku.sku}>
                              <SkuInfoGroup>
                                <SkuCode>{sku.sku}</SkuCode>
                                <SkuDesc title={sku.descricao}>{sku.descricao}</SkuDesc>
                              </SkuInfoGroup>
                              <SkuMetrics>
                                <SkuMetricValue
                                  $accent
                                  title={
                                    sku.media_3m_valor !== undefined && sku.media_3m_valor > 0
                                      ? `Sellout Faturado: ${formatCurrencyBr(sku.valorRS)}\nMédia 3M: ${formatCurrencyBr(sku.media_3m_valor)} (${sku.variacao_valor_percentual !== undefined ? (sku.variacao_valor_percentual > 0 ? '+' : '') + sku.variacao_valor_percentual + '%' : ''})`
                                      : 'Sellout Faturado R$'
                                  }
                                >
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                    <span>{formatCurrencyBr(sku.valorRS)}</span>
                                    {!data.resumo?.isYtd && renderVariationIndicator(skuAlertRS)}
                                  </div>
                                </SkuMetricValue>
                                <SkuMetricValue
                                  title={
                                    sku.media_3m_caixas !== undefined && sku.media_3m_caixas > 0
                                      ? `Sellout Volume: ${sku.caixas.toLocaleString('pt-BR')} cx\nMédia 3M: ${sku.media_3m_caixas.toLocaleString('pt-BR')} cx (${sku.variacao_percentual !== undefined ? (sku.variacao_percentual > 0 ? '+' : '') + sku.variacao_percentual + '%' : ''})`
                                      : 'Sellout Volume CX'
                                  }
                                >
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                    <span>{sku.caixas.toLocaleString('pt-BR')} cx</span>
                                    {!data.resumo?.isYtd && renderVariationIndicator(skuAlertCX)}
                                  </div>
                                </SkuMetricValue>
                                <SkuMetricValue style={{ opacity: 0.7 }} title="Share da Categoria">
                                  {sku.sharePercent}%
                                </SkuMetricValue>
                              </SkuMetrics>
                            </SkuRow>
                          )
                        })}
                      </SkuListContainer>
                    )}
                  </FamiliaRow>
                )
              })
            )}
          </ColumnBody>
        </GridColumn>

        {/* Coluna 2 — Estoque (Posição com quebra por SKU) */}
        <GridColumn>
          <ColumnHeader>
            <div className="header-titles">
              <h2>Estoque</h2>
              <p>Posição de estoque por família</p>
            </div>
          </ColumnHeader>

          <ColumnTotalHeader>
            <ColumnTotalTitle>
              <span>Total Estoque</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '0.72rem' }}>
                  {filteredEstoque.length} {filteredEstoque.length === 1 ? 'família' : 'famílias'}
                </span>
                {filteredEstoque.some((f) => f.skus && f.skus.length > 0) && (
                  <ToggleAllSkusButton type="button" onClick={toggleAllEstoque}>
                    {expandedEstoque.size === filteredEstoque.length ? 'Recolher SKUs' : 'Expandir SKUs'}
                  </ToggleAllSkusButton>
                )}
              </div>
            </ColumnTotalTitle>
            <ColumnTotalMetrics>
              <TotalMetricItem $accent title="Total Estoque CX">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{totals.estoqueCX.toLocaleString('pt-BR')} cx</span>
                  {renderVariationIndicator(totalAlertEstoque)}
                </div>
              </TotalMetricItem>
              <TotalMetricItem title="Total Estoque R$">
                {formatCurrencyBr(totals.estoqueRS)}
              </TotalMetricItem>
            </ColumnTotalMetrics>
          </ColumnTotalHeader>

          <ColumnBody>
            {filteredEstoque.length === 0 ? (
              <EmptyState>
                <FaSearch size={22} />
                Nenhuma família encontrada.
              </EmptyState>
            ) : (
              filteredEstoque.map((item, i) => {
                const hasSkus = Boolean(item.skus && item.skus.length > 0)
                const isExpanded =
                  expandedEstoque.has(item.familia) ||
                  Boolean(
                    term.length > 0 &&
                      item.skus?.some(
                        (s) =>
                          s.sku.toLowerCase().includes(term) ||
                          s.descricao.toLowerCase().includes(term),
                      ),
                  )
                const skusToRender =
                  term.length > 0 && !item.familia.toLowerCase().includes(term)
                    ? (item.skus || []).filter(
                        (s) =>
                          s.sku.toLowerCase().includes(term) ||
                          s.descricao.toLowerCase().includes(term),
                      )
                    : item.skus || []

                return (
                  <FamiliaRow key={item.familia} $index={i}>
                    <FamiliaHeader
                      $clickable={hasSkus}
                      onClick={() => hasSkus && toggleEstoque(item.familia)}
                      title={hasSkus ? (isExpanded ? 'Recolher SKUs' : 'Expandir SKUs') : undefined}
                    >
                      <FamiliaTitleGroup>
                        <FamiliaName>{item.familia}</FamiliaName>
                        {hasSkus && <SkuBadge>{item.skus!.length} SKUs</SkuBadge>}
                      </FamiliaTitleGroup>
                      {hasSkus && (
                        <ExpandChevron $expanded={Boolean(isExpanded)}>
                          <FaChevronDown size={11} />
                        </ExpandChevron>
                      )}
                    </FamiliaHeader>
                    <FamiliaMetrics>
                      <MetricItem $accent>
                        {item.caixas.toLocaleString('pt-BR')} cx
                      </MetricItem>
                      <MetricItem>{formatCurrencyBr(item.valorRS)}</MetricItem>
                    </FamiliaMetrics>
                    {isExpanded && hasSkus && (
                      <SkuListContainer>
                        {skusToRender.map((sku) => {
                          const skuEstAlertCX = getVariationAlert(sku.variacao_percentual)

                          return (
                            <SkuRow key={sku.sku}>
                              <SkuInfoGroup>
                                <SkuCode>{sku.sku}</SkuCode>
                                <SkuDesc title={sku.descricao}>{sku.descricao}</SkuDesc>
                              </SkuInfoGroup>
                              <SkuMetrics>
                                <SkuMetricValue
                                  $accent
                                  title={
                                    sku.media_3m_caixas !== undefined && sku.media_3m_caixas > 0
                                      ? `Estoque Volume: ${sku.caixas.toLocaleString('pt-BR')} cx\nMédia 3M: ${sku.media_3m_caixas.toLocaleString('pt-BR')} cx (${sku.variacao_percentual !== undefined ? (sku.variacao_percentual > 0 ? '+' : '') + sku.variacao_percentual + '%' : ''})`
                                      : 'Estoque Volume CX'
                                  }
                                >
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                    <span>{sku.caixas.toLocaleString('pt-BR')} cx</span>
                                    {!data.resumo?.isYtd && renderVariationIndicator(skuEstAlertCX)}
                                  </div>
                                </SkuMetricValue>
                                <SkuMetricValue title="Estoque Estimado R$">
                                  {formatCurrencyBr(sku.valorRS)}
                                </SkuMetricValue>
                              </SkuMetrics>
                            </SkuRow>
                          )
                        })}
                      </SkuListContainer>
                    )}
                  </FamiliaRow>
                )
              })
            )}
          </ColumnBody>
        </GridColumn>

        {/* Coluna 3 — Cobertura (Clientes positivados com quebra por SKU) */}
        <GridColumn>
          <ColumnHeader>
            <div className="header-titles">
              <h2>Cobertura</h2>
              <p>Clientes positivados por família</p>
            </div>
            {!data.resumo?.isYtd && (
              <ColumnCompareButton
                id="btn-compare-cobertura"
                type="button"
                title="Comparar com Média de 3 Meses"
                onClick={() => setActiveModal('cobertura')}
              >
                <FaScaleBalanced size={15} />
              </ColumnCompareButton>
            )}
          </ColumnHeader>

          <ColumnTotalHeader>
            <ColumnTotalTitle>
              <span>Total Cobertura</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '0.72rem' }}>
                  {filteredCobertura.length} {filteredCobertura.length === 1 ? 'família' : 'famílias'}
                </span>
                {filteredCobertura.some((f) => f.skus && f.skus.length > 0) && (
                  <ToggleAllSkusButton type="button" onClick={toggleAllCobertura}>
                    {expandedCobertura.size === filteredCobertura.length ? 'Recolher SKUs' : 'Expandir SKUs'}
                  </ToggleAllSkusButton>
                )}
              </div>
            </ColumnTotalTitle>
            <ColumnTotalMetrics>
              <TotalMetricItem $accent title="Total Clientes Positivados">
                <span>{(data.resumo?.cobertura_total_clientes ?? totals.coberturaClientes).toLocaleString('pt-BR')} clientes</span>
              </TotalMetricItem>
              {typeof data.resumo?.cobertura_potencial_clientes === 'number' && data.resumo.cobertura_potencial_clientes > 0 && (
                <TotalMetricItem title="Potencial e Gap das Cidades de Atuação">
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: '0.73rem' }}>
                    <span style={{ color: '#a6c10b', fontWeight: 600 }}>
                      Pot: {data.resumo.cobertura_potencial_clientes.toLocaleString('pt-BR')}
                    </span>
                    <span style={{ color: (data.resumo.cobertura_gap_clientes || 0) > 0 ? '#f59e0b' : '#10b981', fontWeight: 600 }}>
                      Gap: {(data.resumo.cobertura_gap_clientes || 0).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </TotalMetricItem>
              )}
            </ColumnTotalMetrics>
          </ColumnTotalHeader>

          <ColumnBody>
            {filteredCobertura.length === 0 ? (
              <EmptyState>
                <FaSearch size={22} />
                Nenhuma família encontrada.
              </EmptyState>
            ) : (
              filteredCobertura.map((item, i) => {
                const maxClientes = Math.max(
                  ...data.coberturaPorFamilia.map((x) => x.clientesPositivados),
                )
                const share =
                  typeof item.percentual === 'number' && !isNaN(item.percentual)
                    ? item.percentual
                    : maxClientes > 0
                      ? parseFloat(((item.clientesPositivados / maxClientes) * 100).toFixed(1))
                      : 0

                const showCategoryPotential =
                  item.potencial_clientes !== undefined &&
                  item.potencial_clientes > 0 &&
                  item.potencial_clientes !== item.clientesPositivados

                const hasSkus = Boolean(item.skus && item.skus.length > 0)
                const isExpanded =
                  expandedCobertura.has(item.familia) ||
                  Boolean(
                    term.length > 0 &&
                      item.skus?.some(
                        (s) =>
                          s.sku.toLowerCase().includes(term) ||
                          s.descricao.toLowerCase().includes(term),
                      ),
                  )
                const skusToRender =
                  term.length > 0 && !item.familia.toLowerCase().includes(term)
                    ? (item.skus || []).filter(
                        (s) =>
                          s.sku.toLowerCase().includes(term) ||
                          s.descricao.toLowerCase().includes(term),
                      )
                    : item.skus || []

                return (
                  <FamiliaRow key={item.familia} $index={i}>
                    <FamiliaHeader
                      $clickable={hasSkus}
                      onClick={() => hasSkus && toggleCobertura(item.familia)}
                      title={hasSkus ? (isExpanded ? 'Recolher SKUs' : 'Expandir SKUs') : undefined}
                    >
                      <FamiliaTitleGroup>
                        <FamiliaName>{item.familia}</FamiliaName>
                        {hasSkus && <SkuBadge>{item.skus!.length} SKUs</SkuBadge>}
                      </FamiliaTitleGroup>
                      {hasSkus && (
                        <ExpandChevron $expanded={Boolean(isExpanded)}>
                          <FaChevronDown size={11} />
                        </ExpandChevron>
                      )}
                    </FamiliaHeader>
                    <FamiliaMetrics>
                      <MetricItem $accent>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{item.clientesPositivados.toLocaleString('pt-BR')} clientes</span>
                          {showCategoryPotential && (
                            <PotentialGapBadge
                              $hasGap={(item.gap_clientes || 0) > 0}
                              title={`Potencial: ${item.potencial_clientes!.toLocaleString('pt-BR')} clientes nas cidades de atuação\nGap: ${(item.gap_clientes || 0).toLocaleString('pt-BR')} clientes a positivar (${item.atingimento_potencial_pct?.toFixed(0) || 0}% atingido)`}
                            >
                              <span>Pot: {item.potencial_clientes!.toLocaleString('pt-BR')}</span>
                              <span>• Gap: {(item.gap_clientes || 0).toLocaleString('pt-BR')}</span>
                            </PotentialGapBadge>
                          )}
                        </div>
                      </MetricItem>
                    </FamiliaMetrics>
                    <ShareBar $percent={share} />
                    {isExpanded && hasSkus && (
                      <SkuListContainer>
                        {skusToRender.map((sku) => {
                          const skuCobAlert = getVariationAlert(sku.variacao_percentual)

                          return (
                            <SkuRow key={sku.sku}>
                              <SkuInfoGroup>
                                <SkuCode>{sku.sku}</SkuCode>
                                <SkuDesc title={sku.descricao}>{sku.descricao}</SkuDesc>
                              </SkuInfoGroup>
                              <SkuMetrics>
                                <SkuMetricValue
                                  $accent
                                  title={
                                    sku.media_3m_clientes !== undefined && sku.media_3m_clientes > 0
                                      ? `Clientes Positivados: ${sku.clientesPositivados.toLocaleString('pt-BR')}\nMédia 3M: ${sku.media_3m_clientes.toLocaleString('pt-BR')} clientes (${sku.variacao_percentual !== undefined ? (sku.variacao_percentual > 0 ? '+' : '') + sku.variacao_percentual + '%' : ''})`
                                      : 'Clientes Positivados'
                                  }
                                >
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                    <span>{sku.clientesPositivados.toLocaleString('pt-BR')} clientes</span>
                                    {!data.resumo?.isYtd && renderVariationIndicator(skuCobAlert)}
                                  </div>
                                </SkuMetricValue>
                              </SkuMetrics>
                            </SkuRow>
                          )
                        })}
                      </SkuListContainer>
                    )}
                  </FamiliaRow>
                )
              })
            )}
          </ColumnBody>
        </GridColumn>
      </MainGrid>

      {/* ── Modal Comparativo: Categorias (Atual × Média de 3 Meses) ────── */}
      {activeModal === 'categorias' && (
        <CompareModalOverlay onClick={() => setActiveModal(null)}>
          <CompareModalContainer onClick={(e) => e.stopPropagation()}>
            <CompareModalHeader>
              <div className="title-group">
                <h3>
                  <FaScaleBalanced size={18} />
                  Comparativo: Categorias (Atual × Média 3 Meses)
                </h3>
                <p>
                  Comparando o sellout atual com as mesmas semanas equivalentes dos 3 meses anteriores fechados
                </p>
              </div>
              <CompareModalCloseBtn
                type="button"
                onClick={() => setActiveModal(null)}
                title="Fechar (ESC)"
              >
                <FaTimes size={15} />
              </CompareModalCloseBtn>
            </CompareModalHeader>

            <CompareModalSummaryCards>
              <CompareSummaryCard>
                <span className="card-label">Sellout Total em R$</span>
                <div className="card-values">
                  <span className="current-val">{formatCurrencyBr(data.totalSelloutRS)}</span>
                  {typeof data.resumo?.sellout_media_3m_valor === 'number' && (
                    <span className="media-val">
                      (Méd. 3M: {formatCurrencyBr(data.resumo.sellout_media_3m_valor)})
                    </span>
                  )}
                </div>
                {renderVariationIndicator(totalAlertRS)}
              </CompareSummaryCard>

              <CompareSummaryCard>
                <span className="card-label">Sellout Total em CX</span>
                <div className="card-values">
                  <span className="current-val">{data.totalSelloutCX.toLocaleString('pt-BR')} cx</span>
                  {typeof data.resumo?.sellout_media_3m_caixas === 'number' && (
                    <span className="media-val">
                      (Méd. 3M: {data.resumo.sellout_media_3m_caixas.toLocaleString('pt-BR')} cx)
                    </span>
                  )}
                </div>
                {renderVariationIndicator(totalAlertCX)}
              </CompareSummaryCard>
            </CompareModalSummaryCards>

            <CompareModalBody>
              <CompareTable>
                <thead>
                  <tr>
                    <th>Família</th>
                    <th className="right-align">Sellout R$ (Atual)</th>
                    <th className="right-align">Média 3M R$</th>
                    <th className="right-align">Var. R$ (%)</th>
                    <th className="right-align">Sellout CX (Atual)</th>
                    <th className="right-align">Média 3M CX</th>
                    <th className="right-align">Var. CX (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.selloutPorFamilia.map((item) => {
                    const alertRS = getVariationAlert(item.variacao_valor_percentual)
                    const alertCX = getVariationAlert(item.variacao_percentual)

                    return (
                      <tr key={item.familia}>
                        <td className="familia-cell">{item.familia}</td>
                        <td className="right-align">{formatCurrencyBr(item.valorRS)}</td>
                        <td className="right-align" style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {item.media_3m_valor ? formatCurrencyBr(item.media_3m_valor) : '—'}
                        </td>
                        <td className="right-align">{renderVariationIndicator(alertRS)}</td>
                        <td className="right-align">{item.caixas.toLocaleString('pt-BR')} cx</td>
                        <td className="right-align" style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {item.media_3m_caixas ? `${item.media_3m_caixas.toLocaleString('pt-BR')} cx` : '—'}
                        </td>
                        <td className="right-align">{renderVariationIndicator(alertCX)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </CompareTable>
            </CompareModalBody>
          </CompareModalContainer>
        </CompareModalOverlay>
      )}

      {/* ── Modal Comparativo: Cobertura (Atual × Média de 3 Meses) ─────── */}
      {activeModal === 'cobertura' && (
        <CompareModalOverlay onClick={() => setActiveModal(null)}>
          <CompareModalContainer onClick={(e) => e.stopPropagation()}>
            <CompareModalHeader>
              <div className="title-group">
                <h3>
                  <FaScaleBalanced size={18} />
                  Comparativo: Cobertura de Clientes (Atual × Média 3 Meses)
                </h3>
                <p>
                  Comparando clientes positivados com a média dos 3 meses anteriores fechados
                </p>
              </div>
              <CompareModalCloseBtn
                type="button"
                onClick={() => setActiveModal(null)}
                title="Fechar (ESC)"
              >
                <FaTimes size={15} />
              </CompareModalCloseBtn>
            </CompareModalHeader>

            <CompareModalSummaryCards>
              <CompareSummaryCard>
                <span className="card-label">Total Clientes Positivados</span>
                <div className="card-values">
                  <span className="current-val">
                    {(data.resumo?.cobertura_total_clientes ?? totals.coberturaClientes).toLocaleString('pt-BR')} clientes
                  </span>
                  {typeof data.resumo?.cobertura_media_3m_clientes === 'number' && (
                    <span className="media-val">
                      (Méd. 3M: {data.resumo.cobertura_media_3m_clientes.toLocaleString('pt-BR')})
                    </span>
                  )}
                </div>
                {renderVariationIndicator(totalAlertCobertura)}
              </CompareSummaryCard>
            </CompareModalSummaryCards>

            <CompareModalBody>
              <CompareTable>
                <thead>
                  <tr>
                    <th>Família</th>
                    <th className="right-align">Clientes Atual</th>
                    <th className="right-align">Média 3M Clientes</th>
                    <th className="right-align">Diferença (Nominal)</th>
                    <th className="right-align">Variação (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.coberturaPorFamilia.map((item) => {
                    const alertCob = getVariationAlert(item.variacao_percentual)
                    const diffNominal =
                      typeof item.variacao_clientes === 'number'
                        ? item.variacao_clientes
                        : typeof item.media_3m_clientes === 'number'
                          ? item.clientesPositivados - item.media_3m_clientes
                          : undefined

                    return (
                      <tr key={item.familia}>
                        <td className="familia-cell">{item.familia}</td>
                        <td className="right-align">{item.clientesPositivados.toLocaleString('pt-BR')}</td>
                        <td className="right-align" style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {item.media_3m_clientes ? item.media_3m_clientes.toLocaleString('pt-BR') : '—'}
                        </td>
                        <td className="right-align" style={{ color: 'rgba(255,255,255,0.7)' }}>
                          {diffNominal !== undefined
                            ? `${diffNominal > 0 ? '+' : ''}${diffNominal.toLocaleString('pt-BR')}`
                            : '—'}
                        </td>
                        <td className="right-align">{renderVariationIndicator(alertCob)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </CompareTable>
            </CompareModalBody>
          </CompareModalContainer>
        </CompareModalOverlay>
      )}
    </PageWrapper>
  )
}
